// frontend/src/pages/WorkedExamples/WorkedExample.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Loader2, 
  ArrowLeft, 
  ChevronLeft, 
  ChevronRight, 
  ThumbsUp, 
  ThumbsDown,
  Bookmark,
  Share2,
  MessageCircle
} from 'lucide-react';
import KaTeX from 'katex';
import 'katex/dist/katex.min.css';
import './WorkedExample.css';

const WorkedExample = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [rating, setRating] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [comment, setComment] = useState('');

  // Fetch worked example details
  const { data: example, isLoading, error } = useQuery({
    queryKey: ['workedExample', id],
    queryFn: async () => {
      const response = await api.get(`/api/worked-examples/${id}`);
      return response.data;
    }
  });

  // Rate example mutation
  const rateMutation = useMutation({
    mutationFn: async (isHelpful) => {
      const response = await api.post(`/api/worked-examples/${id}/rate`, { 
        helpful: isHelpful 
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['workedExample', id]);
    }
  });

  // Bookmark mutation
  const bookmarkMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/api/worked-examples/${id}/bookmark`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['workedExample', id]);
    }
  });

  // Add comment mutation
  const commentMutation = useMutation({
    mutationFn: async (commentText) => {
      const response = await api.post(`/api/worked-examples/${id}/comments`, { 
        content: commentText 
      });
      return response.data;
    },
    onSuccess: () => {
      setComment('');
      queryClient.invalidateQueries(['workedExample', id]);
    }
  });

  // Mark as viewed
  useEffect(() => {
    if (example && user) {
      api.post(`/api/worked-examples/${id}/view`).catch(console.error);
    }
  }, [id, example, user]);

  // Render math content
  const renderMath = (content) => {
    if (!content) return null;
    
    try {
      const processedContent = content
        .replace(/\$\$(.*?)\$\$/g, (match, tex) => {
          const html = KaTeX.renderToString(tex, { displayMode: true, throwOnError: false });
          return `<div class="math-display">${html}</div>`;
        })
        .replace(/\$(.*?)\$/g, (match, tex) => {
          const html = KaTeX.renderToString(tex, { displayMode: false, throwOnError: false });
          return `<span class="math-inline">${html}</span>`;
        });
      
      return <div dangerouslySetInnerHTML={{ __html: processedContent }} />;
    } catch (error) {
      console.error('Math rendering error:', error);
      return <div>{content}</div>;
    }
  };

  // Navigation functions
  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToNextStep = () => {
    if (example && currentStep < example.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Handle rating
  const handleRate = (isHelpful) => {
    setRating(isHelpful);
    rateMutation.mutate(isHelpful);
  };

  // Handle comment submission
  const handleSubmitComment = (e) => {
    e.preventDefault();
    if (comment.trim()) {
      commentMutation.mutate(comment);
    }
  };

  if (isLoading) {
    return (
      <div className="worked-example-loading">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="worked-example-error">
        <p>Error loading worked example. Please try again.</p>
        <button onClick={() => navigate(-1)} className="back-button">
          Go Back
        </button>
      </div>
    );
  }

  if (!example) {
    return null;
  }

  const currentStepData = example.steps[currentStep];

  return (
    <div className="worked-example-container">
      <div className="worked-example-header">
        <button onClick={() => navigate(-1)} className="back-button">
          <ArrowLeft size={20} />
          Back
        </button>
        
        <div className="example-info">
          <span className="grade-badge">Grade {example.grade}</span>
          <span className="topic-badge">{example.topic}</span>
          <span className="subtopic-badge">{example.subtopic}</span>
        </div>
      </div>

      <div className="worked-example-content">
        <h1 className="example-title">{example.title}</h1>
        
        <div className="problem-statement">
          <h2>Problem</h2>
          {renderMath(example.problem.latex || example.problem.text)}
          {example.problem.images?.map((img, index) => (
            <img 
              key={index} 
              src={img} 
              alt={`Problem figure ${index + 1}`} 
              className="problem-image"
            />
          ))}
        </div>

        <div className="solution-section">
          <div className="solution-header">
            <h2>Solution</h2>
            <div className="step-indicator">
              Step {currentStep + 1} of {example.steps.length}
            </div>
          </div>

          <div className="step-content">
            <h3>{currentStepData.title}</h3>
            {renderMath(currentStepData.content.latex || currentStepData.content.text)}
            {currentStepData.content.images?.map((img, index) => (
              <img 
                key={index} 
                src={img} 
                alt={`Step ${currentStep + 1} figure ${index + 1}`} 
                className="step-image"
              />
            ))}
          </div>

          <div className="step-navigation">
            <button 
              onClick={goToPreviousStep} 
              disabled={currentStep === 0}
              className="nav-button"
            >
              <ChevronLeft size={20} />
              Previous
            </button>

            <div className="step-dots">
              {example.steps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`step-dot ${index === currentStep ? 'active' : ''}`}
                  aria-label={`Go to step ${index + 1}`}
                />
              ))}
            </div>

            <button 
              onClick={goToNextStep} 
              disabled={currentStep === example.steps.length - 1}
              className="nav-button"
            >
              Next
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div className="example-actions">
          <div className="rating-section">
            <span>Was this helpful?</span>
            <button 
              onClick={() => handleRate(true)}
              className={`rating-button ${rating === true ? 'active' : ''}`}
              disabled={rateMutation.isLoading}
            >
              <ThumbsUp size={20} />
              <span>{example.helpfulCount || 0}</span>
            </button>
            <button 
              onClick={() => handleRate(false)}
              className={`rating-button ${rating === false ? 'active' : ''}`}
              disabled={rateMutation.isLoading}
            >
              <ThumbsDown size={20} />
              <span>{example.notHelpfulCount || 0}</span>
            </button>
          </div>

          <div className="action-buttons">
            <button 
              onClick={() => bookmarkMutation.mutate()}
              className={`action-button ${example.isBookmarked ? 'active' : ''}`}
            >
              <Bookmark size={20} />
              <span>{example.isBookmarked ? 'Bookmarked' : 'Bookmark'}</span>
            </button>
            
            <button className="action-button">
              <Share2 size={20} />
              <span>Share</span>
            </button>

            <button 
              onClick={() => setShowComments(!showComments)}
              className="action-button"
            >
              <MessageCircle size={20} />
              <span>{example.commentCount || 0}</span>
            </button>
          </div>
        </div>

        {showComments && (
          <div className="comments-section">
            <h3>Comments</h3>
            
            <form onSubmit={handleSubmitComment} className="comment-form">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment..."
                rows={3}
                className="comment-input"
              />
              <button 
                type="submit" 
                disabled={!comment.trim() || commentMutation.isLoading}
                className="comment-submit"
              >
                {commentMutation.isLoading ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  'Post Comment'
                )}
              </button>
            </form>

            <div className="comments-list">
              {example.comments?.map((comment) => (
                <div key={comment._id} className="comment">
                  <div className="comment-header">
                    <img 
                      src={comment.user.avatar || '/default-avatar.png'} 
                      alt={comment.user.username}
                      className="comment-avatar"
                    />
                    <div>
                      <span className="comment-username">{comment.user.username}</span>
                      <span className="comment-time">
                        {new Date(comment.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="comment-content">
                    {renderMath(comment.content)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {example.relatedContent && (
          <div className="related-section">
            <h3>Related Content</h3>
            <div className="related-grid">
              {example.relatedContent.questions?.map((question) => (
                <div 
                  key={question._id} 
                  className="related-item"
                  onClick={() => navigate(`/questions/${question._id}`)}
                >
                  <span className="related-type">Question</span>
                  <p>{question.preview}</p>
                </div>
              ))}
              {example.relatedContent.examples?.map((relatedExample) => (
                <div 
                  key={relatedExample._id} 
                  className="related-item"
                  onClick={() => navigate(`/worked-examples/${relatedExample._id}`)}
                >
                  <span className="related-type">Example</span>
                  <p>{relatedExample.title}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkedExample;